// apps/admin/src/components/shell/AdminTimezoneSelector.tsx
//
// dbg-20260810 — topbar timezone picker.
//
// Design: /home/manhlv/Downloads/figma-v3-timezone-drowdown.pen, frames
// `timezone` (the topbar pill, inside `header-right`) and `timezone-dropdown`
// (the panel). Measurements taken off the node tree, not eyeballed:
//
//   trigger   fill #f1f5f9, r=6, padding 6/10, gap 6
//             globe 14x14 stroke #475569, label 13px/500 #475569, caret 8x4
//   panel     w=260, r=8, 1px #e8e8e8, shadow 0 4 10.5 #0000001a, py=6
//   row       h=36, padding 8/14, gap 10
//             flag 20x14 r=2, name 13px (fills), offset 12px #8c8c8c
//   selected  row fill #faf2ed, name #f06022/500, trailing check #f06022
//
// Colours land on pw-* tokens rather than the raw hexes — the admin ships a
// dark theme and the dark-mode audit rejects hardcoded light values. #f06022
// is the brand orange (pw-primary), #f1f5f9 is pw-surface, #8c8c8c is
// pw-text-muted; the selected row's #faf2ed is that same orange at ~7%.
//
// The design draws the pill to the LEFT of the language selector, which is
// where it is mounted in `AdminTopBar`.
//
// The offset next to each row is computed from the zone at render time
// (`formatUtcOffset`), not taken from the mock's static labels: the mock
// prints US Eastern as UTC-5, which is only true outside daylight time.
//
// Picking a zone routes through `@printway/ui`'s timezone store, which is
// what the app-wide Intl patch reads — so every rendered timestamp follows,
// not just the ones this file knows about. The choice is persisted through
// `usePreferencesStore` so it survives reload and syncs across devices.

import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';

import {
  TIMEZONE_OPTIONS,
  findTimezoneOption,
  formatUtcOffset,
  isSameOffsetGroup,
  isSameTimezone,
  listBasicTimezones,
  searchTimezones,
  useTimezone,
} from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';
import { usePreferencesStore } from '../../stores/usePreferencesStore.js';

// ---------------------------------------------------------------------------
// Exclusive dropdown — same `topbar:dropdown:open` contract the language
// selector and the bell use, so opening this panel collapses the others.
// ---------------------------------------------------------------------------

const TOPBAR_DROPDOWN_EVENT = 'topbar:dropdown:open';
let dropdownIdSeq = 0;

function useExclusiveDropdown(): [boolean, () => void, () => void] {
  const [open, setOpen] = useState(false);
  const idRef = useRef<string>('');
  if (idRef.current === '') {
    dropdownIdSeq += 1;
    idRef.current = `admin-tz-${dropdownIdSeq}`;
  }
  useEffect(() => {
    function onOpen(ev: Event): void {
      const detail = (ev as CustomEvent<{ id: string }>).detail;
      if (detail && detail.id !== idRef.current) setOpen(false);
    }
    function onDocClick(ev: MouseEvent): void {
      if (!open) return;
      const target = ev.target as HTMLElement | null;
      if (target?.closest('[data-admin-tz-root="true"]')) return;
      setOpen(false);
    }
    function onKey(ev: KeyboardEvent): void {
      if (ev.key === 'Escape') setOpen(false);
    }
    // dbg-20260911-093626 — focus-out. This copy already had outside-click and
    // Escape; it was the only one of the five with Escape, and still the only
    // one without this. All five now close the same three ways.
    function onFocusIn(ev: FocusEvent): void {
      if (!open) return;
      const target = ev.target as HTMLElement | null;
      if (target?.closest('[data-admin-tz-root="true"]')) return;
      setOpen(false);
    }
    window.addEventListener(TOPBAR_DROPDOWN_EVENT, onOpen);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      window.removeEventListener(TOPBAR_DROPDOWN_EVENT, onOpen);
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, [open]);

  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      if (next) {
        window.dispatchEvent(
          new CustomEvent(TOPBAR_DROPDOWN_EVENT, { detail: { id: idRef.current } }),
        );
      }
      return next;
    });
  }, []);
  const close = useCallback(() => setOpen(false), []);
  return [open, toggle, close];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdminTimezoneSelector(): JSX.Element {
  const { t } = useTranslation('admin');
  const { timezone, setTimezone } = useTimezone();
  const persistTimezone = usePreferencesStore((s) => s.setTimezone);
  const [open, toggle, close] = useExclusiveDropdown();
  const [query, setQuery] = useState('');

  function handleSelect(zoneId: string): void {
    // Store first: this is what the Intl patch reads, so the screen has
    // already re-rendered in the new zone by the time the PATCH is in flight.
    setTimezone(zoneId);
    // Best-effort persistence — `usePreferencesStore` swallows its own
    // network errors and localStorage keeps the pick across reload.
    persistTimezone(zoneId);
    setQuery('');
    close();
  }

  const searching = query.trim() !== '';
  // ~400 zones behind the box, capped so the panel stays a menu rather than a
  // scroll marathon; the search is how you reach the tail.
  const results = useMemo(() => (searching ? searchTimezones(query, 80) : []), [
    searching,
    query,
  ]);

  // The default list is one zone per offset behaviour, minus the offsets the
  // eight pinned rows already cover — so "+7" appears exactly once
  // (dbg-20260810). Everything else stays reachable through the search box.
  const otherZones = useMemo(
    () =>
      listBasicTimezones().filter(
        (zone) => !TIMEZONE_OPTIONS.some((o) => isSameOffsetGroup(o.id, zone.id)),
      ),
    [],
  );

  return (
    <div className="relative" data-admin-tz-root="true">
      <button
        type="button"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('chrome.timezoneSelector.selectTimezone')}
        data-testid="topbar-timezone-selector"
        className="inline-flex h-9 items-center gap-1.5 rounded-md bg-pw-surface px-2.5 text-[13px] font-medium text-pw-text-muted transition-colors hover:bg-pw-bg-hover hover:text-pw-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pw-primary-strong"
      >
        <GlobeIcon className="h-3.5 w-3.5" aria-hidden />
        <span data-testid="topbar-timezone-offset">{formatUtcOffset(timezone)}</span>
        <Caret />
      </button>

      {open ? (
        <div
          role="menu"
          aria-label={t('chrome.timezoneSelector.selectTimezone')}
          data-testid="topbar-timezone-menu"
          className="absolute right-0 top-full z-50 mt-1 w-[280px] overflow-hidden rounded-lg border border-pw-border bg-pw-bg shadow-lg"
        >
          {/* Search — a plain input, not <SearchInput>: the filtering is over
              an in-memory array, so the 350ms debounce that control exists to
              provide would only make typing feel laggy. */}
          <div className="border-b border-pw-border p-2">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('chrome.timezoneSelector.searchPlaceholder')}
              aria-label={t('chrome.timezoneSelector.searchPlaceholder')}
              data-testid="topbar-timezone-search"
              className="h-8 w-full rounded-md border border-input bg-field px-2.5 text-[13px] text-pw-text outline-none transition-colors placeholder:text-pw-text-muted focus-visible:border-pw-primary"
            />
          </div>

          <div className="max-h-[320px] overflow-y-auto py-1.5">
            {searching ? (
              results.length === 0 ? (
                <p
                  className="px-3.5 py-6 text-center text-[13px] text-pw-text-muted"
                  data-testid="topbar-timezone-empty"
                >
                  {t('chrome.timezoneSelector.noMatch')}
                </p>
              ) : (
                results.map((zone) => {
                  const active = isSameTimezone(zone.id, timezone);
                  return (
                    <button
                      key={zone.id}
                      type="button"
                      role="menuitem"
                      title={zone.id}
                      data-testid={`topbar-timezone-zone-${zone.id}`}
                      onClick={() => handleSelect(zone.id)}
                      className={
                        active
                          ? 'flex h-9 w-full items-center gap-2.5 bg-pw-primary/[0.07] px-3.5 text-left'
                          : 'flex h-9 w-full items-center gap-2.5 px-3.5 text-left transition-colors hover:bg-pw-surface'
                      }
                    >
                      <span
                        className={
                          active
                            ? 'flex-1 truncate text-[13px] font-medium text-pw-primary'
                            : 'flex-1 truncate text-[13px] text-pw-text'
                        }
                      >
                        {zone.label}
                      </span>
                      {/* Same `UTC+1/+2` pair the basic list uses, so a
                          search result reads identically to the row it would
                          have been. */}
                      <span className="shrink-0 text-xs text-pw-text-muted">
                        {zone.offsetRangeLabel ?? zone.offsetLabel}
                      </span>
                      {active ? (
                        <CheckIcon className="h-3 w-3 shrink-0 text-pw-primary" aria-hidden />
                      ) : (
                        <span className="h-3 w-3 shrink-0" aria-hidden />
                      )}
                    </button>
                  );
                })
              )
            ) : (
              <>
                {/* The eight the design draws stay pinned and keep their flags
                    — they are the zones this team actually works in, and
                    burying them 200 rows into an alphabetical list would make
                    the common case the slow one. */}
                {TIMEZONE_OPTIONS.map((option) => {
                  // Alias-tolerant: ICU resolves this machine's zone to the
                  // legacy `Asia/Saigon`, so a strict `===` against
                  // `Asia/Ho_Chi_Minh` highlighted nothing for an operator who
                  // really was on UTC+7.
                  const active = isSameTimezone(option.id, timezone);
                  return (
                    <button
                      key={option.id}
                      type="button"
                      role="menuitem"
                      data-testid={`topbar-timezone-option-${option.key}`}
                      onClick={() => handleSelect(option.id)}
                      className={
                        active
                          ? 'flex h-9 w-full items-center gap-2.5 bg-pw-primary/[0.07] px-3.5 text-left'
                          : 'flex h-9 w-full items-center gap-2.5 px-3.5 text-left transition-colors hover:bg-pw-surface'
                      }
                    >
                      <FlagSwatch region={option.region} />
                      <span
                        className={
                          active
                            ? 'flex-1 truncate text-[13px] font-medium text-pw-primary'
                            : 'flex-1 truncate text-[13px] text-pw-text'
                        }
                      >
                        {t(`chrome.timezoneSelector.zone.${option.key}`)}
                      </span>
                      <span className="shrink-0 text-xs text-pw-text-muted">
                        {formatUtcOffset(option.id)}
                      </span>
                      {active ? (
                        <CheckIcon className="h-3 w-3 shrink-0 text-pw-primary" aria-hidden />
                      ) : (
                        // Keeps the offset column aligned between checked and
                        // unchecked rows — without it every row shifts 10px
                        // when the selection moves.
                        <span className="h-3 w-3 shrink-0" aria-hidden />
                      )}
                    </button>
                  );
                })}

                {/* When the operator's own zone is neither one of the eight
                    nor a basic-list representative, it would otherwise be
                    invisible until they searched for it. */}
                {findTimezoneOption(timezone) === undefined
                && !otherZones.some((z) => isSameTimezone(z.id, timezone)) ? (
                  <div className="mt-1 border-t border-pw-border pt-1">
                    <button
                      type="button"
                      role="menuitem"
                      title={timezone}
                      data-testid="topbar-timezone-current"
                      onClick={() => close()}
                      className="flex h-9 w-full items-center gap-2.5 bg-pw-primary/[0.07] px-3.5 text-left"
                    >
                      <span className="flex-1 truncate text-[13px] font-medium text-pw-primary">
                        {timezone.replace(/_/g, ' ').replace('/', ' / ')}
                      </span>
                      <span className="shrink-0 text-xs text-pw-text-muted">
                        {formatUtcOffset(timezone)}
                      </span>
                      <CheckIcon className="h-3 w-3 shrink-0 text-pw-primary" aria-hidden />
                    </button>
                  </div>
                ) : null}

                {/* The rest of the world, one row per offset. */}
                <div className="mt-1 border-t border-pw-border pt-1">
                  {otherZones.map((zone) => {
                    const active = isSameTimezone(zone.id, timezone);
                    return (
                      <button
                        key={zone.id}
                        type="button"
                        role="menuitem"
                        title={zone.id}
                        data-testid={`topbar-timezone-basic-${zone.id}`}
                        onClick={() => handleSelect(zone.id)}
                        className={
                          active
                            ? 'flex h-9 w-full items-center gap-2.5 bg-pw-primary/[0.07] px-3.5 text-left'
                            : 'flex h-9 w-full items-center gap-2.5 px-3.5 text-left transition-colors hover:bg-pw-surface'
                        }
                      >
                        <span
                          className={
                            active
                              ? 'flex-1 truncate text-[13px] font-medium text-pw-primary'
                              : 'flex-1 truncate text-[13px] text-pw-text'
                          }
                        >
                          {zone.label}
                        </span>
                        {/* `UTC+1/+2` when the zone shifts, so two rows that
                            read the same offset today are visibly different
                            things rather than a de-dupe failure. */}
                        <span className="shrink-0 text-xs text-pw-text-muted">
                          {zone.offsetRangeLabel ?? zone.offsetLabel}
                        </span>
                        {active ? (
                          <CheckIcon className="h-3 w-3 shrink-0 text-pw-primary" aria-hidden />
                        ) : (
                          <span className="h-3 w-3 shrink-0" aria-hidden />
                        )}
                      </button>
                    );
                  })}
                </div>

                <p className="px-3.5 pb-1 pt-2 text-[11px] text-pw-text-muted">
                  {t('chrome.timezoneSelector.searchHint')}
                </p>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default AdminTimezoneSelector;

// ---------------------------------------------------------------------------
// Flag swatch
//
// The design draws each flag as a 20x14 rounded rect with one or two colour
// bands rather than a real flag, so this reproduces those bands. Emoji flags
// were the obvious alternative and are rejected: Windows ships no colour
// flag glyphs, so the column would render as bare "VN" / "US" letter pairs
// on a large share of the operators' machines.
// ---------------------------------------------------------------------------

const FLAG_BANDS: Record<string, { base: string; bands: Array<{ top: string; height: string; color: string }> }> = {
  VN: { base: '#d91717', bands: [{ top: '50%', height: '50%', color: '#ffd600' }] },
  US: { base: '#3b4a8c', bands: [{ top: '50%', height: '50%', color: '#d12938' }] },
  GB: { base: '#00246b', bands: [{ top: '50%', height: '50%', color: '#cf212e' }] },
  JP: { base: '#ffffff', bands: [] },
  DE: {
    base: '#000000',
    bands: [
      { top: '33.3%', height: '33.3%', color: '#de290f' },
      { top: '66.6%', height: '33.4%', color: '#ffcc00' },
    ],
  },
  // dark-ok: the white band of the Australian flag is a flag colour, not a surface
  AU: { base: '#00008c', bands: [{ top: '50%', height: '50%', color: '#ffffff' }] },
  CN: { base: '#de290f', bands: [{ top: '50%', height: '50%', color: '#ffd600' }] },
};

function FlagSwatch({ region }: { region: string }): JSX.Element {
  const spec = FLAG_BANDS[region] ?? FLAG_BANDS['US']!;
  return (
    <span
      aria-hidden="true"
      // Inline styles: these are flag colours, not design tokens, so there is
      // no class to reach for and no dark-mode variant to honour — a national
      // flag is the same colour in either theme.
      // dark-ok: national flag colours are theme-invariant by definition
      className="relative block h-3.5 w-5 shrink-0 overflow-hidden rounded-sm ring-1 ring-black/10"
      style={{ background: spec.base }}
    >
      {spec.bands.map((band) => (
        <span
          key={band.top}
          className="absolute inset-x-0 block"
          style={{ top: band.top, height: band.height, background: band.color }}
        />
      ))}
      {region === 'JP' ? (
        <span
          className="absolute left-1/2 top-1/2 block h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{ background: '#bd122b' }}
        />
      ) : null}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Inline icons — keep this file self-contained, as AdminLanguageSelector does.
// ---------------------------------------------------------------------------

function GlobeIcon(props: { className?: string; 'aria-hidden'?: boolean }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={props.className}
      aria-hidden={props['aria-hidden']}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10Z" />
    </svg>
  );
}

function CheckIcon(props: { className?: string; 'aria-hidden'?: boolean }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={props.className}
      aria-hidden={props['aria-hidden']}
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function Caret(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-3 w-3"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
